<script>
import { mapGetters } from 'vuex'
import { RacePresetUserResource, AssistResource, WeatherPresetResource } from '@/resources/launcher.js'
import CarResource, { CarSkinResource } from '@/resources/car.js'
import TrackResource from '@/resources/track.js'
import ResourceSearchInput from '../../components/ResourceSearchInput.vue'
import EventFormSessions from '../../components/EventFormSessions.vue'

export default {
    components: {
        ResourceSearchInput,
        EventFormSessions,
    },
    computed: {
        ...mapGetters(['userResource']),
        resources() {
            return {
                RacePresetUserResource,
                AssistResource,
                CarResource,
                TrackResource,
                WeatherPresetResource,
            }
        },
        racePresetAttrs() {
            return {
                car: this.car?.id || null,
                skin: this.carSkin?.id || null,
                track: this.track?.id || null,
                assists: this.assist?.id || null,
                weather: this.weatherPreset?.id || null,
                name: this.presetName,
                description: this.presetDescription,
                penalties: this.penalties,
                drift_mode: this.driftMode,
                fixed_setup: this.fixedSetup,
                session_data: this.sessions
            }
        },
        assistButtonProps() {
            if (this.assist) {
                return { value: String(this.assist), variant: 'primary' }
            }
            return { value: 'Select an Assist', variant: 'outline-primary' }
        },
        carSkinCurrentIndex() {
            if (!this.car || !this.carSkin) return 0
            return this.carSkins.findIndex((skin) => skin.id == this.carSkin.id)
        },
        carSkinNext() {
            return this.carSkins[this.carSkinCurrentIndex + 1]
        }
    },
    watch: {
        car() {
            this.fetchCarData()
        },
        track() {
            this.fetchTrackData()
        },
        racePreset() {
            if (this.racePreset) {
                this.fetchExisting()

                if (this.racePreset.id != this.$route.params.id) {
                    this.$router.replace({ name: this.$route.name, params: { id: this.racePreset.id } })
                }
            } else {
                this.reset()
            }
        },
    },
    data() {
        return {
            loading: true,
            racePresets: [],
            racePreset: null,
            presetName: '',
            presetDescription: '',
            presetNameState: null,
            presetDescriptionState: null,
            assists: [],
            assist: null,
            assistPickerModalVisible: false,
            weatherPresets: [],
            weatherPreset: null,
            car: null,
            carSkins: [],
            carSkin: null,
            carImageURL: null,
            track: null,
            trackImageURL: null,
            penalties: true,
            driftMode: false,
            fixedSetup: false,
            sessions: []
        }
    },
    methods: {
        async initialize() {
            const [racePresets, assists, weatherPresets] = await Promise.all([
                RacePresetUserResource.filterAll({ user: this.userResource.id }),
                AssistResource.all(),
                WeatherPresetResource.all()
            ])

            this.racePresets = racePresets
            this.assists = assists
            this.weatherPresets = weatherPresets

            if (this.$route.params.id) {
                this.racePreset = this.racePresets.find((preset) => preset.id == this.$route.params.id)
                await this.fetchExisting()
            } else {
                await Promise.all([
                    this.fetchCarData(),
                    this.fetchTrackData()
                ])
            }

            this.loading = false
        },
        async onSubmit() {
            window.scrollTo(0, 0)

            this.presetNameState = !!this.presetName
            this.presetDescriptionState = !!this.presetDescription

            if (!this.presetNameState || !this.presetDescriptionState) {
                return false
            }

            if (this.racePreset) {
                for (let key in this.racePresetAttrs) {
                    this.racePreset.set(key, this.racePresetAttrs[key])
                }
                await this.racePreset.save()
            } else {
                this.racePreset = new RacePresetUserResource(this.racePresetAttrs)
                this.racePresets.push(this.racePreset)
                await this.racePreset.save()
                this.$router.replace({ name: this.$route.name, params: { id: this.racePreset.id } })
            }

            this.addMessage('success', `Saved ${this.racePreset}`)
        },
        async fetchExisting() {
            if (this.racePreset) {
                await this.racePreset.resolveRelated()
                this.presetName = this.racePreset.attributes.name
                this.presetDescription = this.racePreset.attributes.description
                this.car = this.racePreset.get('car')
                this.track = this.racePreset.get('track')
                this.assist = this.racePreset.get('assists')
                this.weatherPreset = this.weatherPresets.find((wp) => wp.id == this.racePreset.attributes.weather) || null
                this.sessions = this.racePreset.get('session_data')
            }
        },
        async fetchCarData() {
            this.carSkin = null
            this.carSkins = []

            if (this.car) {
                this.carSkins = await CarSkinResource.filterAll({ car: this.car.id })
                this.carSkin = (this.racePreset?.attributes.car == this.car.id)
                    ? this.carSkins.find((skin) => skin.id == this.racePreset?.attributes.skin)
                    : this.carSkins[0]
                this.carImageURL = await this.carSkin.getPreviewImageURL()
            } else {
                this.carImageURL = null
            }
        },
        async fetchTrackData() {
            if (this.track) {
                this.trackImageURL = await this.track.getPreviewImageURL()
            } else {
                this.trackImageURL = null
            }
        },
        async deletePreset() {
            if (this.racePreset) {
                const index = this.racePresets.findIndex((preset) => this.racePreset.id == preset.id)
                this.racePresets.splice(index, 1)
                await this.racePreset.delete()
                this.reset()
            }
        },
        reset() {
            this.racePreset = null
            this.presetName = ''
            this.car = null
            this.track = null
            this.assist = null
            this.weatherPreset = null
            this.$router.replace({ name: this.$route.name, params: { id: null } })
        },
        selectAssist(assist) {
            this.assist = assist
            this.assistPickerModalVisible = false
        },
        async nextSkin() {
            this.carSkin = this.carSkinNext
            this.carImageURL = await this.carSkin.getPreviewImageURL()
        },
        async prevSkin() {
            this.carSkin = this.carSkins[this.carSkinCurrentIndex - 1]
            this.carImageURL = await this.carSkin.getPreviewImageURL()
        },
    },
    async mounted() {
        await this.initialize()
    }
}
</script>

<template>
    <div>
        <div class="mb-4">
            <h4 class="mb-1">Rennvorgaben</h4>
            <p class="text-muted mb-0">Passen Sie Ihr Auto, Ihre Strecke, Ihre Hilfen und Ihre Sitzungseinstellungen an.</p>
        </div>

        <messages />

        <b-form @submit.prevent="onSubmit()">
            <b-card class="mb-3">
                <b-form-group>
                    <template #label>
                        <b-icon icon="collection-play" /> <strong>Ihre Rennvorgaben</strong>
                    </template>
                    <b-input-group>
                        <b-select v-model="racePreset">
                            <b-select-option :value="null">Neu erstellen</b-select-option>
                            <b-select-option v-for="preset in racePresets" :key="preset.id" :value="preset">{{ preset }}</b-select-option>
                        </b-select>
                    </b-input-group>
                </b-form-group>
            </b-card>

            <b-card>
                <b-row>
                    <b-col md="8">
                        <b-form-group>
                            <template #label><strong>Name der Rennvorgabe</strong></template>
                            <b-input v-model="presetName" :state="presetNameState" />
                        </b-form-group>
                    </b-col>
                    <b-col>
                        <b-form-group label="Hilfen">
                            <b-button :variant="assistButtonProps.variant" class="col-sm-12" @click="assistPickerModalVisible = true">{{ assistButtonProps.value }}</b-button>
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-form-group description="Geben Sie Ihrer Vorgabe eine Beschreibung. Diese wird im Sim-Launcher-Bildschirm angezeigt.">
                    <template #label><strong>Beschreibung der Rennvorgabe</strong></template>
                    <b-input v-model="presetDescription" :state="presetDescriptionState" />
                </b-form-group>

                <b-row>
                    <b-col md="6" class="mb-3">
                        <b-form-group>
                            <resource-search-input
                                :to="resources.CarResource"
                                v-model="car"
                                :filter="() => Object.assign({ popular: true })"
                                placeholder="Wählen Sie ein Auto">
                            </resource-search-input>
                        </b-form-group>
                        <div class="picked-image d-flex align-items-center justify-content-center">
                            <b-img v-if="carImageURL" :src="carImageURL" rounded fluid />
                            <span v-else>Keine Autopräferenz</span>
                            <b-link v-if="carSkinCurrentIndex > 0" @click="prevSkin()"><b-icon icon="chevron-left" scale="1.3" /></b-link>
                            <b-link v-if="carSkinNext" @click="nextSkin()" class="right"><b-icon icon="chevron-right" scale="1.3" /></b-link>
                            <small v-if="carSkin" class="skin-name">{{ carSkin }}</small>
                        </div>
                    </b-col>
                    <b-col class="mb-3">
                        <b-form-group>
                            <resource-search-input
                                :to="resources.TrackResource"
                                v-model="track"
                                :filter="() => Object.assign({ popular: true })"
                                placeholder="Wählen Sie eine Strecke">
                            </resource-search-input>
                        </b-form-group>
                        <div class="picked-image d-flex align-items-center justify-content-center">
                            <b-img v-if="trackImageURL" :src="trackImageURL" fluid />
                            <span v-else>Keine Streckenpräferenz</span>
                        </div>
                    </b-col>
                </b-row>

                <hr />

                <b-row>
                    <b-col md="6">
                        <event-form-sessions v-model="sessions" />
                    </b-col>

                    <b-col>
                        <b-form-group label="Wettervorgabe" class="mb-3">
                            <b-select v-model="weatherPreset">
                                <b-select-option :value="null">Keine Präferenz</b-select-option>
                                <b-select-option v-for="wp in weatherPresets" :key="wp.id" :value="wp">{{ wp }}</b-select-option>
                            </b-select>
                        </b-form-group>
                        <b-row>
                            <b-col class="text-center">
                                <b-form-group label="Strafen" class="mb-0" title="Strafen aktivieren. Runden werden möglicherweise nicht aufgezeichnet, wenn dies auf false gesetzt ist" v-b-tooltip.hover>
                                    <b-form-checkbox switch v-model="penalties" />
                                </b-form-group>
                            </b-col>
                            <b-col class="text-center">
                                <b-form-group label="Fest" class="mb-0" title="Festes Setup. Jedes Auto hat die gleiche Standardkonfiguration für Motor, Reifen, Federung usw." v-b-tooltip.hover>
                                    <b-form-checkbox switch v-model="fixedSetup" />
                                </b-form-group>
                            </b-col>
                            <b-col class="text-center">
                                <b-form-group label="Driftmodus" class="mb-0" title="Driftmodus aktivieren. Drifts ausführen, um Punkte zu sammeln. Driftkombos erhöhen Ihren Punktmultiplikator" v-b-tooltip.hover>
                                    <b-form-checkbox switch v-model="driftMode" />
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>

                <hr />

                <b-row>
                    <b-col>
                        <b-button type="submit" variant="primary" class="col-sm-12">Speichern</b-button>
                    </b-col>
                    <b-col v-if="racePreset">
                        <b-button @click="$refs.deleteConfirmModal.show()" variant="outline-danger" class="col-sm-12">Löschen</b-button>
                    </b-col>
                </b-row>
            </b-card>
        </b-form>

        <b-modal title="Sind Sie sicher?" ref="deleteConfirmModal" @ok="deletePreset()" centered no-fade>
            <p>Möchten Sie diese Rennvorgabe wirklich löschen?</p>
        </b-modal>

        <b-modal title="Wählen Sie eine Hilfe" v-model="assistPickerModalVisible" centered no-fade hide-footer>
            <div v-for="assist in assists" :key="assist.id" class="mb-3">
                <p class="font-weight-bold">{{ assist }}</p>
                <b-table-simple bordered responsive>
                    <b-tr>
                        <b-td>Getriebe</b-td>
                        <b-td width="130">{{ assist.attributes.auto_shift ? 'Automatisch' : 'Manuell' }}</b-td>
                    </b-tr>
                    <b-tr>
                        <b-td>Ideale Rennlinie</b-td>
                        <b-td>{{ assist.attributes.ideal_line ? 'An' : 'Aus' }}</b-td>
                    </b-tr>
                    <b-tr>
                        <b-td>Traktionskontrolle</b-td>
                        <b-td>
                            <span v-if="assist.attributes.traction_control == 0">Aus</span>
                            <span v-else-if="assist.attributes.traction_control == 1">Werk</span>
                            <span v-else-if="assist.attributes.traction_control == 2">Erzwungen</span>
                            <span v-else>{{ assist.attributes.traction_control }}</span>
                        </b-td>
                    </b-tr>
                    <b-tr>
                        <b-td>ABS</b-td>
                        <b-td>
                            <span v-if="assist.attributes.abs == 0">Aus</span>
                            <span v-else-if="assist.attributes.abs == 1">Werk</span>
                            <span v-else-if="assist.attributes.abs == 2">Erzwungen</span>
                            <span v-else>{{ assist.attributes.abs }}</span>
                        </b-td>
                    </b-tr>
                    <b-tr>
                        <b-td>Stabilitätskontrolle</b-td>
                        <b-td>{{ assist.attributes.stability_control }}%</b-td>
                    </b-tr>
                    <b-tr>
                        <b-td>Schadensrate</b-td>
                        <b-td>{{ assist.attributes.damage }}%</b-td>
                    </b-tr>
                    <b-tr>
                        <b-td>Reifenverschleißrate</b-td>
                        <b-td>{{ assist.attributes.tire_wear }}</b-td>
                    </b-tr>
                    <b-tr>
                        <b-td>Kraftstoffrate</b-td>
                        <b-td>{{ assist.attributes.fuel_rate }}</b-td>
                    </b-tr>
                    <b-tr>
                        <b-td>Windschatten</b-td>
                        <b-td>{{ assist.attributes.slipstream }}x</b-td>
                    </b-tr>
                </b-table-simple>
                <b-button @click="selectAssist(assist)">Wählen Sie {{ assist }}</b-button>
                <hr />
            </div>
            <b-button v-if="assist" @click="selectAssist(null)" variant="outline-secondary">Auswahl löschen</b-button>
        </b-modal>
    </div>
</template>

<style lang="scss" scoped>
.picked-image {
    text-align: center;
    background: #000;
    border-radius: 6px;
    padding: 10px 0;
    height: 185px;
    color: #fff;
    position: relative;

    img {
        max-height: 165px;
        max-width: 300px;
    }

    a {
        position: absolute;
        top: 45%;
        left: 0;
        z-index: 100;
        color: #fff;

        &.right {
            left: inherit;
            right: 0;
        }
    }

    .skin-name {
        position: absolute;
        bottom: 5px;
        color: #b8b8b8;
    }
}
</style>
